import * as React from 'react'
import { HotKeys } from 'react-hotkeys'
import OutsideClickHandler from 'react-outside-click-handler'

import { BackgroundToLightnessProvider } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import FocusTrap from 'focus-trap-react'

import TetherComponent from 'modules/core/components/TetherComponent'
import type { Attachment } from 'modules/core/components/TetherComponent/types'

import styles from './index.module.css'

interface Props {
  type?: 'wrong' | 'brand'
  size?: 'small' | 'big' | 'bigger' | 'huge' | 'auto'
  darken?: boolean
  offset?: string
  className?: string
  attachment: Attachment
  targetAttachment?: Attachment
  overflowContent?: boolean
  toggle: (value: boolean) => () => void
  open: boolean
  withPreventDefault?: boolean
  disableOutsideClick?: boolean
  focusTrapActive?: boolean
  onOutsideClick?: () => void
}

const cx = classNames.bind(styles)

// TODO: Move to the design system
const Popover = ({
  overflowContent = false,
  darken = false,
  withPreventDefault = false,
  disableOutsideClick = false,
  focusTrapActive = true,
  type,
  size,
  offset,
  className,
  attachment,
  targetAttachment,
  children,
  toggle,
  open,
  onOutsideClick,
}: React.PropsWithChildren<Props>) => {
  const [clickedOutside, setClickedOutside] = React.useState<boolean>(false)
  const childrenArray = React.Children.toArray(children)

  const cancel = () => {
    toggle(false)()
  }

  const onClick = (ev: React.SyntheticEvent<any>) => {
    if (withPreventDefault) ev.preventDefault()

    ev.stopPropagation()

    if (clickedOutside) return // Avoid to close and reopen when clicking on the button

    toggle(!open)()
  }

  const _onOutsideClick = () => {
    onOutsideClick && onOutsideClick()
    setClickedOutside(true)
    setTimeout(() => {
      setClickedOutside(false)
    }, 0)
    cancel()
  }

  const renderPopover = () => {
    if (!open) return null

    return (
      <OutsideClickHandler onOutsideClick={_onOutsideClick} disabled={disableOutsideClick}>
        <FocusTrap
          active={focusTrapActive}
          focusTrapOptions={{
            allowOutsideClick: true,
            fallbackFocus: 'div:first-child' /* No errors if no focusable elements in content */,
          }}
        >
          <div className={className} role="dialog" aria-modal="true" aria-haspopup="dialog">
            <HotKeys
              keyMap={{
                keyEscape: 'escape',
              }}
              handlers={{
                keyEscape: cancel,
              }}
              className={cx('popover', size, type)}
            >
              <div
                className={cx('popoverContent', {
                  overflowContent,
                })}
              >
                {childrenArray[1]}
              </div>
              {attachment !== 'middle center' ? (
                <div
                  className={cx('arrow', type, {
                    darken,
                  })}
                />
              ) : null}
            </HotKeys>
          </div>
        </FocusTrap>
      </OutsideClickHandler>
    )
  }

  const newChildren = React.cloneElement(childrenArray[0] as any, {
    onClick,
    role: 'button',
    tabIndex: '0',
  })

  if (childrenArray.length !== 2) {
    throw new Error('Popover expects two children elements')
  }

  if (!open) return newChildren

  return (
    <TetherComponent
      attachment={attachment}
      offset={offset}
      targetAttachment={targetAttachment}
      type="popover"
    >
      {newChildren}
      <BackgroundToLightnessProvider background={darken ? 'grey1000' : 'grey000White'}>
        {open && renderPopover()}
      </BackgroundToLightnessProvider>
    </TetherComponent>
  )
}

export default Popover
